/**
 * 商家中心（v3.5）—— 链上推广活动（promo）全流程：
 * 入驻条件（stake ≥ merchant_min_stake）→ 创建推广活动（预算托管 PROMO_ESCROW）→
 * 活动列表 / 核销流水 → 消费者核销（五方分账：商家 85% / 推广者 8% / 返现 3% / 金库 2% / 燃烧 2%）。
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { QrCodeView } from '@/components/QrCodeView';
import { container } from '@/services/container';
import { useIdentityStore } from '@/store/identity';
import { useUiStore } from '@/store/ui';
import { formatInt, parseDpb } from '@/lib/format';
import { buildPayUri } from '@/lib/qrPay';
import { MeshLinkError } from '@/lib/result';
import { fetchMerchantProfiles, getMyLocation, saveMerchantProfile } from '@/lib/merchantApi';
import type { Address, PromoCreateReq, PromoRedeemReq } from '@shared/chain';

interface PromoRow {
  promo_id: string;
  from: string;
  budget: number;
  remaining: number;
  coupon_value: number;
  max_uses: number;
  used: number;
  expires_height: number;
  promoter_bps: number;
  created_height: number;
}

interface Redemption {
  promo_id: string;
  from: string;
  amount: number;
  height: number;
  ts: number;
}

const SHARE_RULE = [
  ['商家', '85%', '核销时按活动面额分账给商家'],
  ['推广者', '8%', '推荐人佣金（无推荐人归金库）'],
  ['消费者', '3%', '核销返现'],
  ['金库', '2%', '平台金库'],
  ['燃烧', '2%', '离开流通'],
] as const;

export function MerchantPage(): JSX.Element {
  const address = useIdentityStore((s) => s.address) as Address | undefined;
  // promoList 金额字段为链上 DPB 数值（非微单位），直接格式化展示
  const dpbVal = (v: number | undefined): string =>
    Number(v ?? 0).toLocaleString('zh-CN', { maximumFractionDigits: 4 });
  const [height, setHeight] = useState(0);
  const [stats, setStats] = useState<Record<string, unknown>>({});
  const [promos, setPromos] = useState<PromoRow[]>([]);
  const [reds, setReds] = useState<Redemption[]>([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  // 创建活动表单
  const [budget, setBudget] = useState('100');
  const [coupon, setCoupon] = useState('10');
  const [uses, setUses] = useState('100');
  const [expires, setExpires] = useState('200');
  const [bps, setBps] = useState('800');
  // 消费者核销表单
  const [redeemId, setRedeemId] = useState('');
  const [redeemAmt, setRedeemAmt] = useState('1');
  // v3.31 商家 DPB 抵扣：本店抵扣率（滑杆，链上 min/max 护栏）
  const [deductRate, setDeductRate] = useState('0.15');
  // v3.35 LBS 店铺档案
  const [shopName, setShopName] = useState('');
  const [shopCategory, setShopCategory] = useState('');
  const [shopCity, setShopCity] = useState('');
  const [shopAddr, setShopAddr] = useState('');
  const [shopDesc, setShopDesc] = useState('');
  const [shopLat, setShopLat] = useState('');
  const [shopLng, setShopLng] = useState('');
  const [shopSaved, setShopSaved] = useState(false);
  const [locating, setLocating] = useState(false);
  const [savingShop, setSavingShop] = useState(false);
  // v3.35c 商家入驻：质押保证金（链端 relay_stake，质押后解锁创建推广活动）
  const [stakeAmount, setStakeAmount] = useState('100');
  const [staking, setStaking] = useState(false);

  const params = (stats?.params ?? {}) as Record<string, number>;
  const deductMin = Number(params.merchant_deduct_min ?? 0.03);
  const deductMax = Number(params.merchant_deduct_max ?? 0.3);
  const highReview = Number(params.merchant_deduct_high_review ?? 0.3);
  const subsidyRatio = Number(params.platform_subsidy_ratio ?? 0);
  const subsidyMode = Number(params.subsidy_phase_mode ?? 0);
  const singleCap = Number(params.user_deduct_single_cap ?? 200);
  const deductPct = Math.max(0, Math.min(100, Math.round((Number(deductRate) || 0) * 10000) / 100));
  const needsReview = Number(deductRate) >= highReview - 1e-9;
  const stakes = (stats?.stakes_detail ?? {}) as Record<string, { amount?: number }>;
  const myStake = address ? Number(stakes[address]?.amount ?? 0) : 0;
  const minStake = Number(params.merchant_min_stake ?? 100);
  const qualified = myStake >= minStake;

  const refresh = useCallback(async (): Promise<void> => {
    setBusy(true);
    try {
      const [h, st, pl] = await Promise.all([
        container.chain.getBlockHeight().catch(() => 0),
        container.chain.meshStats().catch(() => ({}) as Record<string, unknown>),
        container.chain.promoList().catch(() => ({ items: [], redemptions: [] }) as Record<string, unknown>),
      ]);
      setHeight(h);
      setStats(st ?? {});
      const items = ((pl?.items ?? []) as PromoRow[]).filter((x) =>
        address ? x.from?.toLowerCase() === address.toLowerCase() : true,
      );
      setPromos(items);
      setReds((pl?.redemptions ?? []) as Redemption[]);
    } catch (err) {
      useUiStore.getState().pushError(err instanceof MeshLinkError ? err.code : 'E_UNKNOWN');
    } finally {
      setBusy(false);
    }
  }, [address]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const createPromo = async (): Promise<void> => {
    if (!address) return;
    const b = parseDpb(budget);
    const c = parseDpb(coupon);
    if (b === '0' || c === '0') {
      setMsg('预算与券面额必须大于 0');
      return;
    }
    setBusy(true);
    try {
      const req: PromoCreateReq = {
        merchant: address,
        budgetMicro: b,
        couponValueMicro: c,
        maxUses: Math.max(1, Math.floor(Number(uses) || 1)),
        expiresHeight: Math.max(0, Math.floor(Number(expires) || 0)),
        promoterBps: Math.max(0, Math.min(4000, Math.floor(Number(bps) || 0))),
        signature: container.identity.signObject({ action: 'promo_create', from: address, ts: Date.now() }),
      };
      await container.chain.promoCreate(req);
      setMsg('推广活动已创建，预算已入链上托管（PROMO_ESCROW）');
      void refresh();
    } catch (err) {
      setMsg(`创建失败：${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setBusy(false);
    }
  };

  const redeemPromo = async (): Promise<void> => {
    if (!address) return;
    const amt = parseDpb(redeemAmt);
    if (!redeemId.trim() || amt === '0') {
      setMsg('请输入推广 ID 与核销金额');
      return;
    }
    setBusy(true);
    try {
      const req: PromoRedeemReq = {
        consumer: address,
        promoId: redeemId.trim(),
        amountMicro: amt,
        signature: container.identity.signObject({ action: 'promo_redeem', from: address, promo_id: redeemId.trim(), ts: Date.now() }),
      };
      await container.chain.promoRedeem(req);
      setMsg('核销成功（五方分账已执行，商家/推广/返现已到账）');
      void refresh();
    } catch (err) {
      setMsg(`核销失败：${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setBusy(false);
    }
  };

  const mineReds = reds.filter((r) =>
    promos.some((p) => p.promo_id === r.promo_id),
  );

  /** v3.34 经营概览：全部由链上 promo/核销流水现算（金额口径：链上 DPB 数值）。 */
  const totalRedeemed = mineReds.reduce((s, r) => s + Number(r.amount ?? 0), 0);
  const redeemedCount = mineReds.length;
  const totalBudget = promos.reduce((s, p) => s + Number(p.budget ?? 0), 0);
  const burnRate = totalBudget > 0 ? (totalRedeemed / totalBudget) * 100 : 0;
  const activePromos = promos.filter((p) => height <= p.expires_height && p.used < p.max_uses).length;
  const estSettled = totalRedeemed * 0.85; // 五方分账：商家 85%

  const copyPromoId = async (pid: string): Promise<void> => {
    try {
      await navigator.clipboard.writeText(pid);
      useUiStore.getState().pushToast('success', '推广码已复制，发给消费者即可核销');
    } catch {
      useUiStore.getState().pushToast('error', '复制失败，请手动复制推广码');
    }
  };

  /** v3.35 裂变优惠券：复制含推荐人的核销链接（好友打开 → 自动绑定 → 核销后我拿 8% 佣金）。 */
  const copyShareLink = async (pid: string): Promise<void> => {
    const ref = address?.toLowerCase() ?? '';
    const link = `https://mesh.doup.cn/redeem?pid=${encodeURIComponent(pid)}&ref=${ref}`;
    try {
      await navigator.clipboard.writeText(link);
      useUiStore.getState().pushToast('success', '优惠券分享链接已复制（好友核销后你获 8% 推广佣金）');
    } catch {
      useUiStore.getState().pushToast('error', '复制失败，请手动复制链接');
    }
  };

  /** v3.35 店铺档案：拉我的档案回填（用于附近商家展示）。 */
  const loadMyProfile = useCallback(async (): Promise<void> => {
    if (!address) return;
    try {
      const list = await fetchMerchantProfiles();
      const mine = list.find((p) => p.address.toLowerCase() === address.toLowerCase());
      if (mine) {
        setShopName(mine.name);
        setShopCategory(mine.category);
        setShopCity(mine.city);
        setShopAddr(mine.addr);
        setShopDesc(mine.desc);
        setShopLat(mine.lat ? String(mine.lat) : '');
        setShopLng(mine.lng ? String(mine.lng) : '');
        setShopSaved(true);
      }
    } catch {
      // 档案服务不可达不阻断商家中心主体功能
    }
  }, [address]);

  useEffect(() => {
    void loadMyProfile();
  }, [loadMyProfile]);

  /** 定位我的经纬度（浏览器 Geolocation，手机/桌面均支持）。 */
  const locateMe = async (): Promise<void> => {
    setLocating(true);
    const loc = await getMyLocation();
    if (loc) {
      setShopLat(loc.lat.toFixed(6));
      setShopLng(loc.lng.toFixed(6));
      useUiStore.getState().pushToast('success', '已获取当前位置');
    } else {
      useUiStore.getState().pushToast('error', '定位失败：请允许位置权限或手动输入经纬度');
    }
    setLocating(false);
  };

  const saveShop = async (): Promise<void> => {
    if (!address) return;
    setSavingShop(true);
    try {
      const profile = await saveMerchantProfile({
        address,
        name: shopName,
        category: shopCategory,
        city: shopCity,
        addr: shopAddr,
        lat: shopLat ? Number(shopLat) : undefined,
        lng: shopLng ? Number(shopLng) : undefined,
        desc: shopDesc,
      });
      setShopName(profile.name);
      setShopLat(profile.lat ? String(profile.lat) : '');
      setShopLng(profile.lng ? String(profile.lng) : '');
      setShopSaved(true);
      useUiStore.getState().pushToast('success', '店铺信息已保存，将出现在「附近商家」');
    } catch (err) {
      useUiStore.getState().pushToast('error', err instanceof Error ? err.message : '保存失败');
    } finally {
      setSavingShop(false);
    }
  };

  /** v3.35c 质押保证金：质押后满足商家门槛即可创建推广活动（服务端签名上链）。 */
  const doStake = async (): Promise<void> => {
    if (!address) return;
    const amt = parseDpb(stakeAmount);
    if (amt === '0') {
      useUiStore.getState().pushToast('error', '质押金额必须大于 0');
      return;
    }
    const peerId = useIdentityStore.getState().peerId || address.toLowerCase().slice(0, 40);
    // v3.37 商家邀请返佣：从邀请链接 ?ref= 读取推荐商家地址（首次质押时绑定）
    const ref = new URLSearchParams(window.location.search).get('ref') ?? undefined;
    setStaking(true);
    try {
      await container.chain.relayStake({
        action: 'stake',
        address,
        amountMicro: amt,
        peerId,
        ...(ref ? { ref: ref as Address } : {}),
      });
      if (ref) {
        useUiStore.getState().pushToast('success',
          `质押成功（${dpbVal(Number(stakeAmount))} DPB），已绑定邀请商家 ${ref.slice(0, 10)}…，其核销商家份额的 5% 将返佣给你`);
      } else {
        useUiStore.getState().pushToast('success', `质押成功（${dpbVal(Number(stakeAmount))} DPB 已入链上托管），已具备商家资格`);
      }
      void refresh();
    } catch (err) {
      useUiStore.getState().pushToast('error', `质押失败：${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setStaking(false);
    }
  };

  /** v3.35c 解押：链端锁定 unstake_lock 块后自动返还。 */
  const doUnstake = async (): Promise<void> => {
    if (!address) return;
    setStaking(true);
    try {
      await container.chain.relayStake({ action: 'unstake', address, amountMicro: '0' });
      useUiStore.getState().pushToast('success', `解押申请已提交：${Number(params.unstake_lock ?? 0)} 块后自动返还质押`);
      void refresh();
    } catch (err) {
      useUiStore.getState().pushToast('error', `解押失败：${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setStaking(false);
    }
  };

  /** v3.35 核销趋势：近 20 块柱状（SVG，无外部依赖）。 */
  const trend = useMemo(() => {
    if (mineReds.length === 0 || height <= 0) return null;
    const buckets = new Map<number, number>();
    for (const r of mineReds) {
      const h = Math.floor(Number(r.height ?? 0));
      buckets.set(h, (buckets.get(h) ?? 0) + 1);
    }
    const start = Math.max(0, height - 20);
    const arr: { h: number; n: number }[] = [];
    for (let h = start; h <= height; h += 1) arr.push({ h, n: buckets.get(h) ?? 0 });
    const max = Math.max(1, ...arr.map((x) => x.n));
    const w = 520;
    const hgt = 96;
    const barW = Math.max(2, Math.min(10, Math.floor(w / arr.length) - 2));
    const bars = arr
      .map((x, i) => {
        const bh = x.n > 0 ? Math.max(3, (x.n / max) * (hgt - 20)) : 0;
        const xPos = 12 + i * ((w - 24) / arr.length);
        return (
          <rect key={x.h} x={xPos} y={hgt - 18 - bh} width={barW} height={bh} rx={1.5}
            fill={x.n > 0 ? '#22c55e' : '#3b5566'} opacity={x.n > 0 ? 0.95 : 0.35}>
            <title>{`高度 ${x.h}：${x.n} 笔核销`}</title>
          </rect>
        );
      });
    return { arr, max, w, hgt, bars };
  }, [mineReds, height]);

  const shareInputCls =
    'w-full rounded-btn bg-panel-2 border border-line px-3 py-2 text-sm text-text placeholder-dim outline-none focus:border-accent';
  const shareLabelCls = 'text-xs text-dim';

  const inputCls =
    'w-full rounded-btn bg-panel-2 border border-line px-3 py-2 text-sm text-text placeholder-dim outline-none focus:border-accent';
  const labelCls = 'text-xs text-dim';
  const deductPayUri = address ? buildPayUri(address, undefined, deductRate) : '';

  return (
    <div className="space-y-4">
      {/* 入驻与规则 */}
      <section className="card p-5">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-sm font-medium text-text">商家中心</h2>
          <Badge tone="ok">链高度 {formatInt(height)}</Badge>
        </div>
        <p className="mt-1 text-xs leading-relaxed text-dim">
          链上推广（promo）机制：商家质押保证金后创建推广活动，预算进入链上托管；
          消费者核销时按五方分账，全程上链、可审计。
        </p>
        <div className="mt-3 grid grid-cols-3 gap-2 text-center">
          <div className="rounded-btn bg-panel-2 px-3 py-2">
            <div className="text-lg font-semibold text-text">{myStake.toFixed(2)}</div>
            <div className="text-[11px] text-dim">我的质押 DPB</div>
          </div>
          <div className="rounded-btn bg-panel-2 px-3 py-2">
            <div className="text-lg font-semibold text-text">{minStake.toFixed(0)}</div>
            <div className="text-[11px] text-dim">商家门槛 DPB</div>
          </div>
          <div className="rounded-btn bg-panel-2 px-3 py-2">
            <div className="text-lg font-semibold text-text">{promos.length}</div>
            <div className="text-[11px] text-dim">我的活动</div>
          </div>
        </div>
        <div className="mt-3">
          {address ? (
            qualified ? (
              <Badge tone="ok">已具备商家资格（质押满足门槛）</Badge>
            ) : (
              <Badge tone="warn">未达商家门槛：质押 {minStake.toFixed(0)} DPB 后即可创建推广活动</Badge>
            )
          ) : (
            <Badge tone="warn">未连接身份</Badge>
          )}
        </div>

        {/* v3.35c 质押保证金：商家入驻入口（原「交易/质押入口」无 UI，此处补全） */}
        {address ? (
          <div className="mt-3 rounded-btn bg-panel-2/60 p-3">
            <div className="mb-2 flex items-center justify-between">
              <span className="text-xs font-medium text-accent">质押保证金（商家入驻）</span>
              <span className="text-[10px] text-dim">
                已质押 {dpbVal(myStake)} / 门槛 {minStake.toFixed(0)} DPB{myStake > 0 ? '（解押需锁 ' + Number(params.unstake_lock ?? 0) + ' 块）' : ''}
              </span>
            </div>
            {myStake < minStake ? (
              <div className="flex gap-2">
                <input
                  className="w-full rounded-btn bg-panel-2 border border-line px-3 py-2 text-sm text-text placeholder-dim outline-none focus:border-accent"
                  type="number"
                  min={minStake}
                  step="any"
                  value={stakeAmount}
                  onChange={(e) => setStakeAmount(e.target.value)}
                  placeholder={`质押 ≥ ${minStake.toFixed(0)} DPB`}
                />
                <Button className="shrink-0" loading={staking} onClick={() => void doStake()}>
                  质押并入驻
                </Button>
              </div>
            ) : (
              <div className="flex items-center justify-between gap-2">
                <span className="text-[11px] text-dim">质押已达标，可自由创建推广活动；如需撤回请解押。</span>
                <Button variant="outline" size="sm" loading={staking} onClick={() => void doUnstake()}>
                  申请解押
                </Button>
              </div>
            )}
          </div>
        ) : null}

        {/* v3.34 经营概览：核销额 / 笔数 / 预算消耗 / 到账预估 */}
        <div className="mt-4 rounded-btn bg-panel-2/60 p-3">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-xs font-medium text-accent">经营概览（链上现算）</span>
            <span className="text-[10px] text-dim">数据源：promo 活动 + 核销流水</span>
          </div>
          <div className="grid grid-cols-2 gap-2 text-center sm:grid-cols-4">
            <div className="rounded-btn bg-panel-2 px-3 py-2">
              <div className="text-lg font-semibold text-text">{dpbVal(totalRedeemed)}</div>
              <div className="text-[11px] text-dim">累计核销 DPB</div>
            </div>
            <div className="rounded-btn bg-panel-2 px-3 py-2">
              <div className="text-lg font-semibold text-text">{redeemedCount}</div>
              <div className="text-[11px] text-dim">核销笔数</div>
            </div>
            <div className="rounded-btn bg-panel-2 px-3 py-2">
              <div className="text-lg font-semibold text-text">{burnRate.toFixed(1)}%</div>
              <div className="text-[11px] text-dim">预算消耗率</div>
            </div>
            <div className="rounded-btn bg-panel-2 px-3 py-2">
              <div className="text-lg font-semibold text-text">{dpbVal(estSettled)}</div>
              <div className="text-[11px] text-dim">预计到账(×85%)</div>
            </div>
          </div>
          <div className="mt-2 flex flex-wrap gap-3 text-[11px] text-dim">
            <span>活动 {promos.length} 个（进行中 {activePromos}）</span>
            <span>总预算 {dpbVal(totalBudget)} DPB</span>
            <span>消耗率 = 累计核销 ÷ 总预算</span>
          </div>
          {trend ? (
            <div className="mt-2">
              <div className="mb-1 flex items-center justify-between text-[11px] text-dim">
                <span>核销趋势（近 20 块 · 笔数）</span>
                <span>峰值 {trend.max} 笔</span>
              </div>
              <svg viewBox={`0 0 ${trend.w} ${trend.hgt}`} className="w-full" role="img" aria-label="核销趋势柱状图">
                <line x1={12} y1={trend.hgt - 18} x2={trend.w - 12} y2={trend.hgt - 18}
                  stroke="#3b5566" strokeWidth={1} />
                {trend.bars}
                <text x={12} y={trend.hgt - 4} fill="#8b93a1" fontSize={9}>
                  {`高度 ${height - 20} → ${height}`}
                </text>
              </svg>
            </div>
          ) : null}
        </div>

        <div className="mt-4">
          <div className="mb-1 text-xs font-medium text-accent">核销五方分账（链上固定）</div>
          <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-5">
            {SHARE_RULE.map(([k, v, d]) => (
              <div key={k} className="rounded-btn bg-panel-2 px-3 py-2">
                <div className="text-xs font-medium text-text">{k} {v}</div>
                <div className="mt-0.5 text-[10px] leading-tight text-dim">{d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* v3.31 DPB 抵扣与平台补贴：商家收银台 */}
      <section className="card p-5">
        <h2 className="text-sm font-medium text-text">DPB 抵扣与平台补贴</h2>
        <p className="mt-1 text-xs leading-relaxed text-dim">
          顾客扫码付款时，可按本店抵扣率用 DPB 抵扣消费金额（商业闭环：DPB 进入真实消费）；
          平台按补贴率向商家补贴抵扣额的等值权益，前期补贴高、成熟后自动退出。
        </p>
        <div className="mt-3 grid grid-cols-2 gap-2 text-center sm:grid-cols-4">
          <div className="rounded-btn bg-panel-2 px-3 py-2">
            <div className="text-lg font-semibold text-text">{(deductMin * 100).toFixed(0)}%–{(deductMax * 100).toFixed(0)}%</div>
            <div className="text-[11px] text-dim">抵扣率区间（链上护栏）</div>
          </div>
          <div className="rounded-btn bg-panel-2 px-3 py-2">
            <div className="text-lg font-semibold text-text">{(subsidyRatio * 100).toFixed(0)}%</div>
            <div className="text-[11px] text-dim">平台补贴率{Number(subsidyRatio) <= 0 ? '（已关闭）' : ''}</div>
          </div>
          <div className="rounded-btn bg-panel-2 px-3 py-2">
            <div className="text-lg font-semibold text-text">{subsidyMode >= 1 ? '自动' : '手动'}</div>
            <div className="text-[11px] text-dim">补贴模式{subsidyMode >= 1 ? '（随商家数衰减）' : '（平台调控）'}</div>
          </div>
          <div className="rounded-btn bg-panel-2 px-3 py-2">
            <div className="text-lg font-semibold text-text">{formatInt(singleCap)}</div>
            <div className="text-[11px] text-dim">单笔抵扣上限 DPB</div>
          </div>
        </div>

        <div className="mt-4 flex flex-col gap-4 sm:flex-row sm:items-start">
          {/* 本店抵扣率设置 */}
          <div className="flex-1 space-y-2">
            <div className="flex items-center justify-between">
              <span className={labelCls}>本店 DPB 抵扣率</span>
              <span className="text-sm font-semibold text-accent">{deductPct.toFixed(1)}%</span>
            </div>
            <input
              type="range"
              min={deductMin}
              max={deductMax}
              step={0.01}
              value={deductRate}
              onChange={(e) => setDeductRate(e.target.value)}
              className="w-full accent-accent"
            />
            <div className="flex justify-between text-[10px] text-dim">
              <span>{(deductMin * 100).toFixed(0)}%</span>
              <span>建议 {(Math.min(deductMax, 0.2) * 100).toFixed(0)}%</span>
              <span>{(deductMax * 100).toFixed(0)}%</span>
            </div>
            <p className="text-[11px] leading-relaxed text-dim">
              {needsReview
                ? `⚠ 抵扣率 ≥ ${(highReview * 100).toFixed(0)}% 为高抵扣档，需平台审核后生效，防止大额套利。`
                : '抵扣率越高，顾客用 DPB 抵扣越多，获客越强；平台补贴按抵扣额比例发放给商家。'}
            </p>
            <p className="text-[11px] text-dim">
              复购衰减：老顾客在同店复购时抵扣额按 ×{Number(params.user_deduct_decay ?? 0.9).toFixed(2)} 系数递减（刺激新客、控老客套利）。
            </p>
          </div>

          {/* 商家收银台码 */}
          <div className="flex shrink-0 flex-col items-center gap-2 rounded-card border border-line bg-panel-2/50 p-4">
            {address ? (
              <>
                <QrCodeView value={deductPayUri} size={180} copyable label={`商家收银台码（DPB 抵扣 ${deductPct.toFixed(1)}%）`} />
                <p className="max-w-[200px] text-center text-[11px] leading-relaxed text-dim">
                  顾客扫此码付款：输入消费金额，自动按 {deductPct.toFixed(1)}% 抵扣 DPB，其余法币线下/聚合支付结算。
                </p>
              </>
            ) : (
              <p className="text-xs text-dim">连接身份后生成商家收银台码</p>
            )}
          </div>
        </div>
      </section>

      {/* v3.35 店铺信息（LBS 附近商家）：档案存服务端，展示名/品类/位置 */}
      <section className="card p-5">
        <div className="mb-1 flex items-center justify-between">
          <h2 className="text-sm font-medium text-text">店铺信息（附近商家展示）</h2>
          {shopSaved ? <Badge tone="ok">已保存</Badge> : <Badge tone="warn">未设置</Badge>}
        </div>
        <p className="mb-3 text-xs leading-relaxed text-dim">
          设置店铺名称、品类与位置后，你的推广活动将按距离出现在消费者「发现 → 商家优惠」的
          「附近商家」列表（定位开启时优先按距离排序）。
        </p>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          <label className="space-y-1">
            <span className={shareLabelCls}>店铺名称</span>
            <input className={shareInputCls} value={shopName} onChange={(e) => setShopName(e.target.value)} placeholder="如：老王糖水铺" maxLength={40} />
          </label>
          <label className="space-y-1">
            <span className={shareLabelCls}>品类</span>
            <input className={shareInputCls} value={shopCategory} onChange={(e) => setShopCategory(e.target.value)} placeholder="如：餐饮 / 零售" maxLength={20} />
          </label>
          <label className="space-y-1">
            <span className={shareLabelCls}>城市</span>
            <input className={shareInputCls} value={shopCity} onChange={(e) => setShopCity(e.target.value)} placeholder="如：深圳" maxLength={20} />
          </label>
          <label className="space-y-1 sm:col-span-2">
            <span className={shareLabelCls}>详细地址</span>
            <input className={shareInputCls} value={shopAddr} onChange={(e) => setShopAddr(e.target.value)} placeholder="如：南山区科技园 1 号店" maxLength={80} />
          </label>
          <div className="flex items-end gap-1.5">
            <Button className="flex-1" variant="outline" size="sm" loading={locating} onClick={() => void locateMe()}>
              获取我的位置
            </Button>
          </div>
          <label className="space-y-1">
            <span className={shareLabelCls}>纬度 (WGS84)</span>
            <input className={shareInputCls} type="number" step="any" value={shopLat} onChange={(e) => setShopLat(e.target.value)} placeholder="如 22.540503" />
          </label>
          <label className="space-y-1">
            <span className={shareLabelCls}>经度 (WGS84)</span>
            <input className={shareInputCls} type="number" step="any" value={shopLng} onChange={(e) => setShopLng(e.target.value)} placeholder="如 113.934528" />
          </label>
          <label className="space-y-1 sm:col-span-2">
            <span className={shareLabelCls}>店铺简介</span>
            <input className={shareInputCls} value={shopDesc} onChange={(e) => setShopDesc(e.target.value)} placeholder="一句话介绍你的店（最多 200 字）" maxLength={200} />
          </label>
          <div className="flex items-end">
            <Button className="w-full" size="sm" loading={savingShop} disabled={!address} onClick={() => void saveShop()}>
              保存店铺信息
            </Button>
          </div>
        </div>

        {/* v3.37 店铺名片二维码海报：扫一扫直达店铺（地址+店名），方便线下物料投放 */}
        <div className="mt-4 flex flex-col items-center gap-2 rounded-card border border-line bg-panel-2/50 p-4 sm:flex-row sm:justify-between">
          {address ? (
            <>
              <div className="flex flex-col items-center gap-1.5">
                <QrCodeView
                  value={`https://mesh.doup.cn/merchant?addr=${encodeURIComponent(address)}${shopName ? `&name=${encodeURIComponent(shopName)}` : ''}`}
                  size={168}
                  copyable
                  label={`店铺名片（${shopName || '未命名店铺'}）`}
                />
                <p className="max-w-[220px] text-center text-[11px] leading-relaxed text-dim">
                  打印/分享此名片码：好友扫码直达你的店铺，可查看活动、核销与返佣。
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => void (async () => {
                  const link = `https://mesh.doup.cn/merchant?addr=${encodeURIComponent(address)}${shopName ? `&name=${encodeURIComponent(shopName)}` : ''}`;
                  try {
                    await navigator.clipboard.writeText(link);
                    useUiStore.getState().pushToast('success', '店铺名片链接已复制，可发到群聊/朋友圈');
                  } catch {
                    useUiStore.getState().pushToast('error', '复制失败，请手动复制');
                  }
                })()}
              >
                复制名片链接
              </Button>
            </>
          ) : (
            <p className="text-xs text-dim">连接身份后生成店铺名片码</p>
          )}
        </div>
      </section>

      {/* v3.37 P0-1 商家邀请返佣：邀请好友商家入驻，其核销商家份额 5% 返佣给你（链上自动拆分） */}
      <section className="card p-5">
        <div className="mb-1 flex items-center justify-between">
          <h2 className="text-sm font-medium text-text">商家邀请返佣（推荐好友商家入驻）</h2>
          <Badge tone="ok">5% 返佣</Badge>
        </div>
        <p className="mb-3 text-xs leading-relaxed text-dim">
          把你的邀请链接发给同行/好友商家：对方通过链接质押入驻后，其推广活动的每次核销，
          商家份额的 <b className="text-text">5%</b> 将链上自动拆给你（1440 块冷静期后生效，全程可审计）。
          邀请越多、被邀商家生意越好，你的被动返佣越多。
        </p>
        <div className="rounded-btn bg-panel-2/60 p-3">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-xs font-medium text-accent">我的商家邀请码（=我的地址）</span>
            {address ? <Badge tone="ok">已连接</Badge> : <Badge tone="warn">未连接身份</Badge>}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <code className="min-w-0 flex-1 truncate rounded-btn bg-panel-1 px-3 py-2 font-mono text-[11px] text-text">
              {address ?? '—'}
            </code>
            <Button
              variant="outline"
              size="sm"
              disabled={!address}
              onClick={() => void (async () => {
                const link = `https://mesh.doup.cn/merchant?ref=${encodeURIComponent(address?.toLowerCase() ?? '')}`;
                try {
                  await navigator.clipboard.writeText(link);
                  useUiStore.getState().pushToast('success', '商家邀请链接已复制：好友质押入驻时自动绑定你为推荐人');
                } catch {
                  useUiStore.getState().pushToast('error', '复制失败，请手动复制链接');
                }
              })()}
            >
              复制邀请链接
            </Button>
          </div>
          <p className="mt-2 text-[11px] leading-relaxed text-dim">
            被邀商家打开链接 → 质押入驻 → 链上绑定 merchant_referrals →
            其后每次核销按 5% 拆分返佣给你（冷静期内归金库防刷）。
          </p>
        </div>
      </section>

      {/* v3.37 P0-2 回头券：消费即返，下次核销自动抵扣（金库补贴，人人可得） */}
      <section className="card p-5">
        <div className="mb-1 flex items-center justify-between">
          <h2 className="text-sm font-medium text-text">回头券（消费返利 · 自动抵扣）</h2>
          <Badge tone="ok">已上线</Badge>
        </div>
        <p className="mb-3 text-xs leading-relaxed text-dim">
          全链行为奖励之外的新增长钩子：任何消费者在任意商家核销成功一次，链上自动发放一张
          <b className="text-text">回头券（0.5 DPB，由平台金库补贴）</b>；下次核销时按券值自动抵扣，
          先到先抵扣，每人同时持 1 张、核销即换新，有效期 1440 块（约 4 小时）。
        </p>
        <div className="grid grid-cols-2 gap-2 text-center sm:grid-cols-4">
          <div className="rounded-btn bg-panel-2 px-3 py-2">
            <div className="text-lg font-semibold text-text">0.5</div>
            <div className="text-[11px] text-dim">回头券面额 DPB</div>
          </div>
          <div className="rounded-btn bg-panel-2 px-3 py-2">
            <div className="text-lg font-semibold text-text">1 张</div>
            <div className="text-[11px] text-dim">每人持有上限</div>
          </div>
          <div className="rounded-btn bg-panel-2 px-3 py-2">
            <div className="text-lg font-semibold text-text">1440 块</div>
            <div className="text-[11px] text-dim">有效期（约 4 小时）</div>
          </div>
          <div className="rounded-btn bg-panel-2 px-3 py-2">
            <div className="text-lg font-semibold text-text">金库</div>
            <div className="text-[11px] text-dim">补贴来源（平台补贴）</div>
          </div>
        </div>
        <p className="mt-3 text-[11px] leading-relaxed text-dim">
          商家侧：回头券抵扣部分由金库出资补回活动托管，不削减你的分账；消费者侧：消费即返、复购自动省，
          形成「消费 → 返券 → 再消费」闭环。治理参数可在超管理台调整（revisit_enabled / revisit_value / revisit_lock_blocks）。
        </p>
      </section>

      {/* 创建推广活动 */}
      <section className="card p-5">
        <h2 className="mb-3 text-sm font-medium text-text">创建推广活动</h2>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          <label className="space-y-1">
            <span className={labelCls}>预算 (DPB)</span>
            <input className={inputCls} type="number" min="0.000001" step="any" value={budget} onChange={(e) => setBudget(e.target.value)} placeholder="如 100" />
          </label>
          <label className="space-y-1">
            <span className={labelCls}>券面额 (DPB/次)</span>
            <input className={inputCls} type="number" min="0.000001" step="any" value={coupon} onChange={(e) => setCoupon(e.target.value)} placeholder="如 10" />
          </label>
          <label className="space-y-1">
            <span className={labelCls}>核销次数上限</span>
            <input className={inputCls} type="number" min="1" value={uses} onChange={(e) => setUses(e.target.value)} placeholder="如 100" />
          </label>
          <label className="space-y-1">
            <span className={labelCls}>有效期（相对当前高度 +N 块）</span>
            <input className={inputCls} type="number" min="1" value={expires} onChange={(e) => setExpires(e.target.value)} placeholder="如 200" />
          </label>
          <label className="space-y-1">
            <span className={labelCls}>推广分成 (bps，≤4000=40%)</span>
            <input className={inputCls} type="number" min="0" max="4000" value={bps} onChange={(e) => setBps(e.target.value)} placeholder="如 800" />
          </label>
          <div className="flex items-end">
            <Button className="w-full" loading={busy} disabled={!address || !qualified} onClick={() => void createPromo()}>
              创建并托管
            </Button>
          </div>
        </div>
        {msg ? <p className="mt-3 text-xs text-accent">{msg}</p> : null}
      </section>

      {/* 我的推广活动 */}
      <section className="card p-5">
        <h2 className="mb-3 text-sm font-medium text-text">我的推广活动（{promos.length}）</h2>
        {promos.length === 0 ? (
          <EmptyState title="还没有推广活动" description="创建活动后，预算进入链上托管并生成推广 ID（卡券码）" />
        ) : (
          <div className="space-y-2">
            {promos.map((p) => {
              const alive = height <= p.expires_height && p.used < p.max_uses;
              return (
                <div key={p.promo_id} className="rounded-btn bg-panel-2 px-3 py-2.5">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="font-mono text-xs text-text">#{p.promo_id.slice(0, 16)}</div>
                    {alive ? <Badge tone="ok">进行中</Badge> : <Badge tone="warn">已结束</Badge>}
                  </div>
                  <div className="mt-1 grid grid-cols-3 gap-1 text-[11px] text-dim sm:grid-cols-6">
                    <span>预算 {dpbVal(p.budget)}</span>
                    <span>券值 {dpbVal(p.coupon_value)}</span>
                    <span>核销 {p.used}/{p.max_uses}</span>
                    <span>剩余 {dpbVal(p.remaining)}</span>
                    <span>有效期至 {formatInt(p.expires_height)}</span>
                    <span>推广 {((p.promoter_bps ?? 0) / 100).toFixed(1)}%</span>
                  </div>
                  <div className="mt-1.5 flex flex-wrap items-center justify-between gap-2">
                    <span className="font-mono text-[10px] text-dim">推广码（消费者核销用）：{p.promo_id}</span>
                    <div className="flex items-center gap-1.5">
                      {alive ? (
                        <>
                          <Button variant="outline" size="sm" onClick={() => void copyPromoId(p.promo_id)}>
                            复制核销码
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => void copyShareLink(p.promo_id)}>
                            分享券（带推荐）
                          </Button>
                        </>
                      ) : null}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* 消费者核销 */}
      <section className="card p-5">
        <h2 className="mb-3 text-sm font-medium text-text">消费核销</h2>
        <p className="mb-3 text-xs text-dim">输入推广码（promo_id）与消费金额，核销后五方分账自动执行，消费者获得返现。</p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <label className="space-y-1">
            <span className={labelCls}>推广码 promo_id</span>
            <input className={inputCls} value={redeemId} onChange={(e) => setRedeemId(e.target.value)} placeholder="16 位 hex 推广码" />
          </label>
          <label className="space-y-1">
            <span className={labelCls}>核销金额 (DPB)</span>
            <input className={inputCls} type="number" min="0.000001" step="any" value={redeemAmt} onChange={(e) => setRedeemAmt(e.target.value)} placeholder="如 1" />
          </label>
          <div className="flex items-end">
            <Button className="w-full" variant="outline" loading={busy} disabled={!address} onClick={() => void redeemPromo()}>
              核销
            </Button>
          </div>
        </div>
      </section>

      {/* 核销流水 */}
      <section className="card p-5">
        <h2 className="mb-3 text-sm font-medium text-text">相关核销流水（{mineReds.length}）</h2>
        {mineReds.length === 0 ? (
          <EmptyState title="暂无核销记录" description="消费者核销后，分账流水将在此展示" />
        ) : (
          <div className="space-y-2">
            {mineReds.slice().reverse().map((r, i) => (
              <div key={i} className="flex flex-wrap items-center justify-between gap-2 rounded-btn bg-panel-2 px-3 py-2 text-xs">
                <span className="font-mono text-text">#{r.promo_id.slice(0, 16)}</span>
                <span className="text-dim">{dpbVal(r.amount)} DPB</span>
                <span className="font-mono text-dim">{String(r.from ?? '').slice(0, 12)}…</span>
                <span className="text-dim">高度 {formatInt(r.height ?? 0)}</span>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

export default MerchantPage;
